/**
 * Color Identity data for all 32 MTG color identity combinations.
 *
 * Keys are WUBRG-sorted uppercase strings (e.g. '', 'W', 'WU', 'WUB', 'WUBRG').
 * Icons use mana-font: ms-cost for mono/colorless, ms-ci color wheels for multi.
 */
import type { ReactElement } from 'react';

import type { FilterDropdownOption, FilterDropdownOptionGroup } from '@/components/FilterDropdown/FilterDropdown';

// ============================================================================
// Types
// ============================================================================

interface ColorIdentityEntry {
  /** WUBRG-sorted key (e.g. 'WU') */
  key: string;
  /** Display name (e.g. 'Azorius') */
  label: string;
  /** Individual color names for search */
  colorNames: string[];
  /** mana-font icon class(es) */
  iconClass: string;
  /** Whether this uses ms-ci (color identity wheel) styling */
  isCiIcon: boolean;
  /** Group label */
  group: string;
  /** Badge class for selected pills in trigger */
  badgeClassName: string;
}

// ============================================================================
// Constants
// ============================================================================

const GROUPS = {
  COLORLESS: 'Colorless',
  MONO: 'Mono',
  GUILD: 'Guild Pairs',
  SHARD: 'Shards & Wedges',
  FOUR: 'Four-Color',
  FIVE: 'Five-Color',
} as const;

const BADGE = {
  colorless: 'bg-zinc-500/20 text-zinc-400',
  white: 'bg-yellow-200/30 text-yellow-200',
  blue: 'bg-blue-400/20 text-blue-400',
  black: 'bg-zinc-400/20 text-zinc-300',
  red: 'bg-red-400/20 text-red-400',
  green: 'bg-green-400/20 text-green-400',
  multi: 'bg-amber-400/20 text-amber-400',
} as const;

// All 32 color identity entries in display order
const ENTRIES: ColorIdentityEntry[] = [
  // Colorless
  {
    key: '',
    label: 'Colorless',
    colorNames: ['colorless'],
    iconClass: 'ms-c ms-cost',
    isCiIcon: false,
    group: GROUPS.COLORLESS,
    badgeClassName: BADGE.colorless,
  },

  // Mono
  {
    key: 'W',
    label: 'White',
    colorNames: ['white'],
    iconClass: 'ms-w ms-cost',
    isCiIcon: false,
    group: GROUPS.MONO,
    badgeClassName: BADGE.white,
  },
  {
    key: 'U',
    label: 'Blue',
    colorNames: ['blue'],
    iconClass: 'ms-u ms-cost',
    isCiIcon: false,
    group: GROUPS.MONO,
    badgeClassName: BADGE.blue,
  },
  {
    key: 'B',
    label: 'Black',
    colorNames: ['black'],
    iconClass: 'ms-b ms-cost',
    isCiIcon: false,
    group: GROUPS.MONO,
    badgeClassName: BADGE.black,
  },
  {
    key: 'R',
    label: 'Red',
    colorNames: ['red'],
    iconClass: 'ms-r ms-cost',
    isCiIcon: false,
    group: GROUPS.MONO,
    badgeClassName: BADGE.red,
  },
  {
    key: 'G',
    label: 'Green',
    colorNames: ['green'],
    iconClass: 'ms-g ms-cost',
    isCiIcon: false,
    group: GROUPS.MONO,
    badgeClassName: BADGE.green,
  },

  // Guild Pairs (2-color)
  {
    key: 'WU',
    label: 'Azorius',
    colorNames: ['white', 'blue'],
    iconClass: 'ms-ci ms-ci-2 ms-ci-wu',
    isCiIcon: true,
    group: GROUPS.GUILD,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'WB',
    label: 'Orzhov',
    colorNames: ['white', 'black'],
    iconClass: 'ms-ci ms-ci-2 ms-ci-wb',
    isCiIcon: true,
    group: GROUPS.GUILD,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'UB',
    label: 'Dimir',
    colorNames: ['blue', 'black'],
    iconClass: 'ms-ci ms-ci-2 ms-ci-ub',
    isCiIcon: true,
    group: GROUPS.GUILD,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'UR',
    label: 'Izzet',
    colorNames: ['blue', 'red'],
    iconClass: 'ms-ci ms-ci-2 ms-ci-ur',
    isCiIcon: true,
    group: GROUPS.GUILD,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'BR',
    label: 'Rakdos',
    colorNames: ['black', 'red'],
    iconClass: 'ms-ci ms-ci-2 ms-ci-br',
    isCiIcon: true,
    group: GROUPS.GUILD,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'BG',
    label: 'Golgari',
    colorNames: ['black', 'green'],
    iconClass: 'ms-ci ms-ci-2 ms-ci-bg',
    isCiIcon: true,
    group: GROUPS.GUILD,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'RG',
    label: 'Gruul',
    colorNames: ['red', 'green'],
    iconClass: 'ms-ci ms-ci-2 ms-ci-rg',
    isCiIcon: true,
    group: GROUPS.GUILD,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'WR',
    label: 'Boros',
    colorNames: ['white', 'red'],
    iconClass: 'ms-ci ms-ci-2 ms-ci-wr',
    isCiIcon: true,
    group: GROUPS.GUILD,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'WG',
    label: 'Selesnya',
    colorNames: ['white', 'green'],
    iconClass: 'ms-ci ms-ci-2 ms-ci-wg',
    isCiIcon: true,
    group: GROUPS.GUILD,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'UG',
    label: 'Simic',
    colorNames: ['blue', 'green'],
    iconClass: 'ms-ci ms-ci-2 ms-ci-ug',
    isCiIcon: true,
    group: GROUPS.GUILD,
    badgeClassName: BADGE.multi,
  },

  // Shards & Wedges (3-color)
  {
    key: 'WUB',
    label: 'Esper',
    colorNames: ['white', 'blue', 'black'],
    iconClass: 'ms-ci ms-ci-3 ms-ci-esper',
    isCiIcon: true,
    group: GROUPS.SHARD,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'UBR',
    label: 'Grixis',
    colorNames: ['blue', 'black', 'red'],
    iconClass: 'ms-ci ms-ci-3 ms-ci-grixis',
    isCiIcon: true,
    group: GROUPS.SHARD,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'BRG',
    label: 'Jund',
    colorNames: ['black', 'red', 'green'],
    iconClass: 'ms-ci ms-ci-3 ms-ci-jund',
    isCiIcon: true,
    group: GROUPS.SHARD,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'WRG',
    label: 'Naya',
    colorNames: ['white', 'red', 'green'],
    iconClass: 'ms-ci ms-ci-3 ms-ci-naya',
    isCiIcon: true,
    group: GROUPS.SHARD,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'WUG',
    label: 'Bant',
    colorNames: ['white', 'blue', 'green'],
    iconClass: 'ms-ci ms-ci-3 ms-ci-bant',
    isCiIcon: true,
    group: GROUPS.SHARD,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'WBG',
    label: 'Abzan',
    colorNames: ['white', 'black', 'green'],
    iconClass: 'ms-ci ms-ci-3 ms-ci-abzan',
    isCiIcon: true,
    group: GROUPS.SHARD,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'WUR',
    label: 'Jeskai',
    colorNames: ['white', 'blue', 'red'],
    iconClass: 'ms-ci ms-ci-3 ms-ci-jeskai',
    isCiIcon: true,
    group: GROUPS.SHARD,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'UBG',
    label: 'Sultai',
    colorNames: ['blue', 'black', 'green'],
    iconClass: 'ms-ci ms-ci-3 ms-ci-sultai',
    isCiIcon: true,
    group: GROUPS.SHARD,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'WBR',
    label: 'Mardu',
    colorNames: ['white', 'black', 'red'],
    iconClass: 'ms-ci ms-ci-3 ms-ci-mardu',
    isCiIcon: true,
    group: GROUPS.SHARD,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'URG',
    label: 'Temur',
    colorNames: ['blue', 'red', 'green'],
    iconClass: 'ms-ci ms-ci-3 ms-ci-temur',
    isCiIcon: true,
    group: GROUPS.SHARD,
    badgeClassName: BADGE.multi,
  },

  // Four-Color
  {
    key: 'UBRG',
    label: 'Non-White',
    colorNames: ['blue', 'black', 'red', 'green'],
    iconClass: 'ms-ci ms-ci-4 ms-ci-glint',
    isCiIcon: true,
    group: GROUPS.FOUR,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'WBRG',
    label: 'Non-Blue',
    colorNames: ['white', 'black', 'red', 'green'],
    iconClass: 'ms-ci ms-ci-4 ms-ci-dune',
    isCiIcon: true,
    group: GROUPS.FOUR,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'WURG',
    label: 'Non-Black',
    colorNames: ['white', 'blue', 'red', 'green'],
    iconClass: 'ms-ci ms-ci-4 ms-ci-ink',
    isCiIcon: true,
    group: GROUPS.FOUR,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'WUBG',
    label: 'Non-Red',
    colorNames: ['white', 'blue', 'black', 'green'],
    iconClass: 'ms-ci ms-ci-4 ms-ci-witch',
    isCiIcon: true,
    group: GROUPS.FOUR,
    badgeClassName: BADGE.multi,
  },
  {
    key: 'WUBR',
    label: 'Non-Green',
    colorNames: ['white', 'blue', 'black', 'red'],
    iconClass: 'ms-ci ms-ci-4 ms-ci-yore',
    isCiIcon: true,
    group: GROUPS.FOUR,
    badgeClassName: BADGE.multi,
  },

  // Five-Color
  {
    key: 'WUBRG',
    label: 'Five-Color',
    colorNames: ['white', 'blue', 'black', 'red', 'green'],
    iconClass: 'ms-ci ms-ci-5',
    isCiIcon: true,
    group: GROUPS.FIVE,
    badgeClassName: BADGE.multi,
  },
];

// ============================================================================
// Exports
// ============================================================================

/** WUBRG sort order for building identity keys */
const WUBRG_ORDER = ['W', 'U', 'B', 'R', 'G'] as const;

/** Sort a Color[] into a WUBRG-ordered key string */
export function toColorIdentityKey(colors: string[]): string {
  return WUBRG_ORDER.filter((c) => colors.includes(c)).join('');
}

const ICON_STYLE = { fontSize: '16px' } as const;

function buildIcon(entry: ColorIdentityEntry): ReactElement {
  return <i className={`ms ${entry.iconClass}`} style={ICON_STYLE} aria-hidden="true" />;
}

function buildSearchText(entry: ColorIdentityEntry): string {
  // Combine: label + color names + key letters (space-separated for each) + group
  const keyLetters = entry.key.split('').map((c) => c.toLowerCase());
  return [entry.label, ...entry.colorNames, entry.key.toLowerCase(), ...keyLetters, entry.group].join(' ').toLowerCase();
}

/** Group definitions for FilterDropdown */
export const COLOR_IDENTITY_GROUPS: FilterDropdownOptionGroup[] = [
  { groupName: GROUPS.COLORLESS },
  { groupName: GROUPS.MONO },
  { groupName: GROUPS.GUILD },
  { groupName: GROUPS.SHARD },
  { groupName: GROUPS.FOUR },
  { groupName: GROUPS.FIVE },
];

/** Pre-built FilterDropdownOption[] for all 32 color identities */
export const COLOR_IDENTITY_OPTIONS: FilterDropdownOption[] = ENTRIES.map((entry) => ({
  value: entry.key,
  label: entry.label,
  icon: buildIcon(entry),
  badgeClassName: entry.badgeClassName,
  group: entry.group,
  searchText: buildSearchText(entry),
}));
